import React from "react";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { SxProps, Theme } from "@mui/material/styles";
import { renderToStaticMarkup } from "react-dom/server";

const BRAND_PRIMARY = "#3f51b5";
const BRAND_PRIMARY_DARK = "#3443a0";
const BRAND_PRIMARY_LIGHT = "#eef2ff";
const CHAT_BORDER = "#dfe5f0";
const CHAT_BACKGROUND = "#fbfcff";
const MUTED_TEXT = "#7a8599";
const TEND_BE_SUBTLE_SHADOW = "0 0 1px rgba(53, 52, 52, 0.3294117647)";

const createAutoAwesomeIconSrc = () => {
    const iconMarkup = renderToStaticMarkup(<AutoAwesomeOutlinedIcon />);
    const svgMarkup = iconMarkup.match(/<svg[\s\S]*<\/svg>/)?.[0] ?? "";
    const coloredSvgMarkup = svgMarkup
        .replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"')
        .replace(/class="[^"]*"/g, "")
        .replace(/style="[^"]*"/g, "")
        .replace("<path", `<path fill="${BRAND_PRIMARY}"`);

    return `data:image/svg+xml;utf8,${encodeURIComponent(coloredSvgMarkup)}`;
};

const autoAwesomeIconSrc = createAutoAwesomeIconSrc();

const createFileDownloadIconSrc = () => {
    const iconMarkup = renderToStaticMarkup(<FileDownloadOutlinedIcon />);
    const svgMarkup = iconMarkup.match(/<svg[\s\S]*<\/svg>/)?.[0] ?? "";
    const cleanedSvgMarkup = svgMarkup
        .replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"')
        .replace(/class="[^"]*"/g, "")
        .replace(/style="[^"]*"/g, "");

    return `data:image/svg+xml;utf8,${encodeURIComponent(cleanedSvgMarkup)}`;
};

export const deepChatExportIconSrc = createFileDownloadIconSrc();

export const assistantTabPaperSx: SxProps<Theme> = {
    display: "flex",
    flexDirection: "column",
    height: {
        xs: "clamp(420px, 72svh, 680px)",
        sm: "clamp(420px, calc(100dvh - 262px), 760px)",
    },
    minHeight: { xs: 420, sm: 420 },
    borderRadius: { xs: 2, sm: 3 },
    overflow: "hidden",
    bgcolor: CHAT_BACKGROUND,
    border: "none",
    boxShadow: `${TEND_BE_SUBTLE_SHADOW} !important`,
    p: 0,
    mt: { xs: 1.5, sm: 2 },
    mb: { xs: 1.5, sm: 2 },
    "& deep-chat": {
        border: "none !important",
        boxShadow: `${TEND_BE_SUBTLE_SHADOW} !important`,
    },
};

export const assistantTabHeaderSx: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    flexShrink: 0,
    gap: 1,
    px: { xs: 2, sm: 2.5 },
    py: 1,
    borderBottom: `1px solid ${CHAT_BORDER}`,
    backgroundColor: "#ffffff",
};

export const assistantComparisonTriggerSx: SxProps<Theme> = {
    minWidth: { xs: 0, sm: "fit-content" },
    width: { xs: "100%", sm: "auto" },
    maxWidth: "100%",
    minHeight: 40,
    justifyContent: "space-between",
    px: 1.5,
    py: 0.75,
    borderRadius: "999px",
    borderColor: CHAT_BORDER,
    color: "#1f2937",
    backgroundColor: "#ffffff",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.2,
    textTransform: "none",
    boxShadow: "none",
    whiteSpace: { xs: "normal", sm: "nowrap" },
    textAlign: "left",
    "&:hover": {
        borderColor: BRAND_PRIMARY,
        backgroundColor: BRAND_PRIMARY_LIGHT,
        boxShadow: "none",
    },
    "&.Mui-disabled": {
        borderColor: CHAT_BORDER,
        color: "#9aa4b8",
    },
};

export const assistantTabLoadingBoxSx: SxProps<Theme> = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
};

export const assistantTabChatBodySx: SxProps<Theme> = {
    flex: 1,
    minHeight: 0,
};

export const assistantComparisonPopoverPaperSx: SxProps<Theme> = {
    mt: -1,
    width: { xs: "calc(100vw - 24px)", sm: 388 },
    maxWidth: "calc(100vw - 24px)",
    maxHeight: { xs: "min(70svh, 520px)", sm: "min(70vh, 560px)" },
    overflowY: "auto",
    borderRadius: 3,
    border: "1px solid #e4e9f2",
    backgroundColor: "#ffffff",
    boxShadow:
        "0 18px 44px rgba(15, 23, 42, 0.12), 0 4px 14px rgba(15, 23, 42, 0.08)",
    p: 1.5,
};

export const assistantComparisonPopoverContentSx: SxProps<Theme> = {
    display: "flex",
    flexDirection: "column",
    gap: 1.25,
};

export const assistantComparisonPopoverTitleSx: SxProps<Theme> = {
    px: 0.5,
    fontSize: "0.875rem",
    fontWeight: 700,
    color: "#111827",
};

export const assistantComparisonListSx: SxProps<Theme> = {
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
};

export const assistantComparisonEventRowSx: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    minWidth: 0,
    borderRadius: 2,
    px: 1,
    py: 0.875,
    border: "1px solid #e7edf6",
    bgcolor: "#f8fbff",
};

export const assistantComparisonEventIconSx: SxProps<Theme> = {
    fontSize: 18,
    color: "#3f51b5",
};

export const assistantComparisonEventTitleSx: SxProps<Theme> = {
    flex: 1,
    fontWeight: 600,
    minWidth: 0,
    overflowWrap: "anywhere",
};

export const assistantComparisonCurrentBadgeSx: SxProps<Theme> = {
    flexShrink: 0,
    borderRadius: "999px",
    px: 0.75,
    py: 0.25,
    bgcolor: "#eef2ff",
    color: "#4050b5",
    fontSize: "0.7rem",
    fontWeight: 700,
    lineHeight: 1.2,
};

export const assistantComparisonRemoveButtonSx: SxProps<Theme> = {
    color: "#7a8599",
    ml: "auto",
    "&:hover": {
        backgroundColor: "#edf2f7",
        color: "#334155",
    },
};

export const assistantComparisonSearchFieldSx: SxProps<Theme> = {
    "& .MuiOutlinedInput-root": {
        minHeight: 42,
        borderRadius: 2.5,
        backgroundColor: "#ffffff",
        "& fieldset": {
            borderColor: "#d8e0ed",
        },
        "&:hover fieldset": {
            borderColor: "#b9c7db",
        },
        "&.Mui-focused fieldset": {
            borderColor: BRAND_PRIMARY,
            boxShadow: "0 0 0 3px rgba(63, 81, 181, 0.08)",
        },
    },
    "& .MuiInputBase-input": {
        fontSize: "0.875rem",
        py: 1.15,
    },
};

export const assistantComparisonSearchResultsSx: SxProps<Theme> = {
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
};

export const assistantComparisonSearchResultButtonSx: SxProps<Theme> = {
    justifyContent: "flex-start",
    minWidth: 0,
    overflowWrap: "anywhere",
    borderRadius: 2,
    px: 1,
    py: 0.9,
    border: "1px solid #e7edf6",
    color: "#1f2937",
    backgroundColor: "#ffffff",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "none",
    "& .MuiButton-startIcon": {
        marginRight: 1,
        color: BRAND_PRIMARY,
    },
    "&:hover": {
        borderColor: "#c9d5e7",
        backgroundColor: "#f8fbff",
        boxShadow: "none",
    },
};

export const assistantComparisonSearchStateSx: SxProps<Theme> = {
    px: 0.5,
    py: 0.25,
    color: MUTED_TEXT,
    fontSize: "0.8125rem",
};

export const createDeepChatTextInput = (placeholderText: string) =>
    ({
        styles: {
            container: {
                borderRadius: "999px",
                border: `1px solid ${CHAT_BORDER}`,
                boxShadow: "0 8px 18px rgba(31, 41, 55, 0.04)",
                width: "calc(100% - 68px)",
                minHeight: "54px",
                marginLeft: "0",
                marginBottom: "18px",
                backgroundColor: "#ffffff",
            },
            text: {
                padding: "16px 116px 16px 26px",
                color: "#1f2937",
                fontSize: "13px",
                lineHeight: "22px",
            },
            focus: {
                border: `1px solid ${BRAND_PRIMARY}`,
                boxShadow: "0 0 0 3px rgba(63, 81, 181, 0.12)",
            },
        },
        placeholder: {
            text: placeholderText,
            style: { color: "#9aa4b8" },
        },
        characterLimit: 2000,
    } as const);

export const deepChatSubmitButtonStyles = {
    position: "inside-end",
    submit: {
        container: {
            default: {
                width: "44px",
                height: "44px",
                minWidth: "44px",
                minHeight: "44px",
                borderRadius: "50%",
                backgroundColor: BRAND_PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "0",
                boxShadow: "0 8px 16px rgba(63, 81, 181, 0.22)",
            },
            hover: {
                backgroundColor: BRAND_PRIMARY_DARK,
            },
            click: {
                backgroundColor: BRAND_PRIMARY_DARK,
                transform: "scale(0.96)",
            },
        },
        svg: {
            styles: {
                default: {
                    width: "22px",
                    height: "22px",
                    filter: "brightness(0) invert(1)",
                },
            },
        },
    },
    disabled: {
        container: {
            default: {
                backgroundColor: "#d8deeb",
                boxShadow: "none",
            },
        },
        svg: {
            styles: {
                default: {
                    filter: "brightness(0) invert(1)",
                    opacity: "0.9",
                },
            },
        },
    },
    loading: {
        container: {
            default: {
                backgroundColor: BRAND_PRIMARY,
            },
        },
    },
} as const;

export const deepChatAvatars = {
    default: {
        styles: {
            position: "start",
            container: {
                marginLeft: "0",
                marginRight: "10px",
            },
        },
    },
    ai: {
        src: autoAwesomeIconSrc,
        styles: {
            position: "start",
            container: {
                width: "44px",
                height: "44px",
                minWidth: "44px",
                borderRadius: "50%",
                border: `1px solid ${CHAT_BORDER}`,
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "0",
                marginRight: "10px",
                marginTop: "4px",
                boxShadow: "0 6px 14px rgba(31, 41, 55, 0.06)",
            },
            avatar: {
                width: "25px",
                height: "25px",
                paddingTop: "0",
                borderRadius: "0",
            },
        },
    },
    user: {
        styles: {
            container: { display: "none" },
        },
    },
} as const;

export const createDeepChatNames = (aiName: string) =>
    ({
        ai: { text: aiName },
        user: { text: " " },
    } as const);

export const deepChatMessageStyles = {
    default: {
        shared: {
            outerContainer: {
                width: "100%",
            },
            bubble: {
                maxWidth: "100%",
                backgroundColor: "unset",
                marginTop: "0",
                marginBottom: "0",
                color: "#1f2937",
                fontSize: "13px",
                lineHeight: "24px",
                padding: "0",
            },
        },
        user: {
            outerContainer: {
                flexDirection: "row-reverse",
                marginBottom: "6px",
                justifyContent: "flex-start",
                paddingRight: "8px",
                paddingLeft: "8px",
                boxSizing: "border-box",
            },
            innerContainer: {
                borderRadius: "14px",
                backgroundColor: BRAND_PRIMARY_LIGHT,
                maxWidth: "calc(100% - 16px)",
                padding: "14px 18px",
                boxSizing: "border-box",
            },
            bubble: {
                color: "#1f2937",
            },
        },
        ai: {
            outerContainer: {
                marginBottom: "6px",
                paddingLeft: "8px",
                paddingRight: "8px",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                boxSizing: "border-box",
            },
            innerContainer: {
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                border: `1px solid ${CHAT_BORDER}`,
                boxShadow: "0 10px 24px rgba(31, 41, 55, 0.04)",
                maxWidth: "calc(100% - 72px)",
                padding: "20px 24px",
                boxSizing: "border-box",
            },
        },
    },
    intro: {
        outerContainer: {
            marginBottom: "6px",
            justifyContent: "flex-start",
        },
        innerContainer: {
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            border: `1px solid ${CHAT_BORDER}`,
            boxShadow: "0 10px 24px rgba(31, 41, 55, 0.04)",
            maxWidth: "calc(100% - 18px)",
            padding: "20px 24px",
            boxSizing: "border-box",
        },
    },
} as const;

export const deepChatStyle = {
    display: "block",
    width: "100%",
    height: "100%",
    border: "none !important",
    minHeight: 0,
    borderRadius: "inherit",
    backgroundColor: CHAT_BACKGROUND,
    boxShadow: `${TEND_BE_SUBTLE_SHADOW} !important`,
} as const;

export const deepChatAuxiliaryStyle = `
  #container,
    #chat-view {
    overflow-x: hidden;
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
  }

  #messages {
        padding: 22px 8px 0;
      overflow-x: hidden;
    box-sizing: border-box;
  }

    .deep-chat-outer-container-role-ai,
    .deep-chat-outer-container-role-user,
    .deep-chat-outer-container-role-ai > div,
    .deep-chat-outer-container-role-user > div {
        margin-inline: 0 !important;
    }

    .deep-chat-outer-container-role-user > div {
        width: fit-content !important;
        max-width: calc(100% - 16px) !important;
        margin-left: auto !important;
    }

    .deep-chat-outer-container-role-user .message-bubble {
        width: auto !important;
        max-width: 100% !important;
    }

    .deep-chat-outer-container-role-ai {
        position: relative;
        align-items: flex-start !important;
    }

    .deep-chat-outer-container-role-ai .avatar-container.start-item-position {
        margin-right: 10px !important;
        margin-left: 0 !important;
        margin-top: 4px !important;
    }

  .inside-end {
    inset-inline-end: 20px;
    inset-block-end: 24px;
  }

  #text-input-container {
    box-sizing: border-box;
    position: relative;
    width: 100%;
    padding-bottom: env(safe-area-inset-bottom);
  }

    .tendbe-chat-export-button {
        position: absolute;
        inset-inline-end: 72px;
        inset-block-end: 6px;
        width: 44px;
        height: 44px;
        min-width: 44px;
        min-height: 44px;
        padding: 0;
        line-height: 0;
        border-radius: 50%;
        border: none;
        border-color: transparent;
        background:none;
        color: #3f3f3f;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: none;
        cursor: pointer;
        transition: background-color 0.15s ease;
    }

    .tendbe-chat-export-button:hover {
        background: #e9e9e1;
    }

    .tendbe-chat-export-button:disabled {
        cursor: not-allowed;
        opacity: 0.65;
    }

    .tendbe-chat-export-button img {
        display: block;
        width: 22px;
        height: 22px;
        filter: brightness(0) saturate(100%) invert(28%) sepia(8%) saturate(220%) hue-rotate(178deg) brightness(96%) contrast(87%);
        pointer-events: none;
    }

  #text-input:focus:empty:before {
    content: "";
  }

  .name {
    display: none;
  }

    .message-bubble.ai-message.ai-message-text.text-message {
        padding-top: 7px !important;
    }

  .tendbe-chat-date-divider {
    color: ${MUTED_TEXT};
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    margin: 0 0 18px;
  }

  .tendbe-chat-timestamp {
    color: ${MUTED_TEXT};
    font-size: 13px;
    line-height: 18px;
    margin-bottom: 24px;
  }

  .tendbe-chat-timestamp-ai {
      margin-left: 68px;
  }

  .tendbe-chat-timestamp-user {
    margin-right: 18px;
    text-align: right;
  }

  .tendbe-chat-checkmark {
    color: ${BRAND_PRIMARY};
    font-weight: 600;
    margin-left: 8px;
  }

  @media (max-width: 768px) {
        #messages {
            padding: 14px 4px 0;
        }

        #text-input {
            font-size: 16px !important;
            padding: 14px 112px 14px 18px !important;
        }

        #text-input-container {
            width: calc(100% - 32px) !important;
            min-height: 50px !important;
            margin-bottom: 14px !important;
        }

        .inside-end {
            inset-inline-end: 14px;
            inset-block-end: 20px;
        }

        .tendbe-chat-export-button {
            inset-inline-end: 62px;
            inset-block-end: 6px;
            width: 44px;
            height: 44px;
            min-width: 44px;
            min-height: 44px;
        }

        .tendbe-chat-export-button img {
            width: 20px;
            height: 20px;
        }

        .deep-chat-outer-container-role-user > div {
            max-width: calc(100% - 16px) !important;
        }

    .deep-chat-outer-container-role-ai > div {
            max-width: calc(100% - 72px) !important;
    }

        .deep-chat-outer-container-role-ai .message-bubble {
            padding: 0 !important;
        }

    .tendbe-chat-timestamp-ai {
            margin-left: 68px;
    }

    .tendbe-chat-timestamp-user {
      margin-right: 18px;
    }
  }
`;
